import * as THREE from 'three';
import { TEAM } from '../game/MatchAuthority.js';

/**
 * Capture points, drawn in the world and summarised on the HUD.
 *
 * Deliberately separate from the mode that owns them. `Domination` decides who
 * holds what; this reads `objective:zones` off the bus and draws it, exactly like
 * every other consumer of the event vocabulary. That split is what lets a second
 * objective mode reuse the whole visual layer by publishing the same event, and it
 * keeps meshes out of a rules file.
 *
 * ## What a player has to be able to tell at a glance
 *
 * Three things, in this order of urgency: **where** the point is (you are usually
 * not looking at it), **whose** it is, and **whether it is moving**. So:
 *
 *   - a ground ring at the real capture radius, so "am I on it" is a question the
 *     floor answers rather than the HUD;
 *   - a vertical beam, because the ring is invisible from anywhere but nearby and
 *     the flag you need is usually behind a building;
 *   - a letter chip per point along the top of the screen, coloured by owner, with
 *     a fill that tracks capture progress.
 *
 * Contested is its own state and reads as a stutter rather than a colour, because
 * "two teams are standing here" is the one thing you must not confuse with "it is
 * being taken from you".
 */

export const TUNING = {
  ringSegments: 48,
  ringWidth: 0.35,
  beamHeight: 9,
  beamRadius: 0.16,
  /** Ground rings lift this far to avoid z-fighting with the street. */
  lift: 0.06,
  colors: {
    [TEAM.ALPHA]: 0x74c7ff,
    [TEAM.BRAVO]: 0xff8a6a,
    [TEAM.NONE]: 0xd8d2c4,
  },
  contestedHz: 5,
};

function colorFor(team) {
  return TUNING.colors[team] ?? TUNING.colors[TEAM.NONE];
}

/**
 * The same colour as a CSS string.
 *
 * Needed because `style.background = 0xff8a6a` is not an error — the browser
 * discards the invalid value and silently keeps whatever was there before, so the
 * capture bar rendered transparent while every log line said it had been coloured.
 */
function cssColorFor(team) {
  return `#${colorFor(team).toString(16).padStart(6, '0')}`;
}

export class Objectives {
  constructor(ctx) {
    this.ctx = ctx;
    this.zones = [];
    this._nodes = [];
    this._t = 0;

    this.ringGeo = new THREE.RingGeometry(1 - TUNING.ringWidth, 1, TUNING.ringSegments);
    this.ringGeo.rotateX(-Math.PI / 2);
    this.beamGeo = new THREE.CylinderGeometry(
      TUNING.beamRadius, TUNING.beamRadius, TUNING.beamHeight, 8, 1, true
    );
    this.beamGeo.translate(0, TUNING.beamHeight / 2, 0);

    this._buildHud();

    this._offs = [
      ctx.events.on('objective:zones', (p) => this._onZones(p)),
      ctx.events.on('objective:captured', (p) => this._onCaptured(p)),
      ctx.events.on('objective:neutralised', (p) => this._onNeutralised(p)),
    ];

    ctx.engine.registerUpdate((dt) => this.update(dt), 'fx');
  }

  /* --------------------------------- HUD ---------------------------------- */

  _buildHud() {
    const host = document.getElementById('hud') ?? document.body;
    const el = document.createElement('div');
    el.className = 'obj-strip hud-fade';
    el.hidden = true;
    host.appendChild(el);
    this.hudEl = el;
    this._chips = new Map();
  }

  _chip(id) {
    let c = this._chips.get(id);
    if (c) return c;
    c = document.createElement('div');
    c.className = 'obj-chip';
    c.innerHTML = `<i></i><b>${id}</b>`;
    this.hudEl.appendChild(c);
    this._chips.set(id, c);
    return c;
  }

  /* -------------------------------- world --------------------------------- */

  _node(i) {
    let n = this._nodes[i];
    if (n) return n;
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    const ring = new THREE.Mesh(this.ringGeo, mat);
    ring.renderOrder = 990;
    ring.frustumCulled = false;
    // The beam shares the ring's material so a colour change is one write, and so
    // the two can never disagree about who owns the point.
    const beam = new THREE.Mesh(this.beamGeo, mat);
    beam.renderOrder = 990;
    beam.frustumCulled = false;
    this.ctx.scene.add(ring, beam);
    n = { ring, beam, mat };
    this._nodes[i] = n;
    return n;
  }

  _onZones({ zones }) {
    this.zones = zones ?? [];
    if (this.zones.length && this.hudEl.hidden) {
      this.hudEl.hidden = false;
      document.getElementById('hud')?.classList.add('has-obj');
    }
  }

  _onCaptured({ zone, team }) {
    const mine = team === this.ctx.match?.teamOf(this.ctx.player);
    this.ctx.events.emit('ui:banner', {
      key: mine ? 'banner.captured' : 'banner.lost',
      params: { zone },
      tone: mine ? 'good' : 'bad',
    });
    this.ctx.events.emit('audio:play', { id: mine ? 'ui_kill' : 'ui_select', volume: 0.6 });
  }

  _onNeutralised({ zone }) {
    // `notice`: not a kill, so it must not wear a kill row's "YOU · weapon" prefix.
    this.ctx.events.emit('ui:killfeed', {
      key: 'feed.neutralised', params: { zone }, notice: true,
    });
  }

  update(dt) {
    this._t += dt;
    // A square wave, not a sine: contested has to read as an alarm, and a smooth
    // pulse is indistinguishable from the capture animation at a glance.
    const blink = Math.floor(this._t * TUNING.contestedHz) % 2 === 0;

    for (let i = 0; i < this.zones.length; i++) {
      const z = this.zones[i];
      const n = this._node(i);
      n.ring.position.set(z.position.x, z.position.y + TUNING.lift, z.position.z);
      n.ring.scale.setScalar(z.radius);
      n.beam.position.set(z.position.x, z.position.y + TUNING.lift, z.position.z);

      const owner = z.owner;
      const shown = z.contested && blink ? TEAM.NONE : owner;
      n.mat.color.setHex(colorFor(shown));
      n.mat.opacity = z.contested ? (blink ? 0.75 : 0.3) : owner === TEAM.NONE ? 0.4 : 0.6;
      n.ring.visible = true;
      n.beam.visible = true;

      /* --- chip --- */
      const chip = this._chip(z.id);
      const mineTeam = this.ctx.match?.teamOf(this.ctx.player) ?? TEAM.ALPHA;
      chip.classList.toggle('is-ours', owner === mineTeam);
      chip.classList.toggle('is-theirs', owner !== TEAM.NONE && owner !== mineTeam);
      chip.classList.toggle('is-contested', !!z.contested);
      // The bar always fills from empty toward whoever is taking it, so its length
      // means "how close is this to changing hands" rather than "who is winning".
      const fill = Math.abs(z.progress);
      chip.firstElementChild.style.transform = `scaleX(${fill.toFixed(3)})`;
      chip.firstElementChild.style.background =
        z.capturing == null ? 'transparent' : cssColorFor(z.capturing);
    }

    for (let i = this.zones.length; i < this._nodes.length; i++) {
      const n = this._nodes[i];
      n.ring.visible = false;
      n.beam.visible = false;
    }
  }

  dispose() {
    for (const off of this._offs) off();
    for (const n of this._nodes) {
      this.ctx.scene.remove(n.ring, n.beam);
      n.mat.dispose();
    }
    this._nodes.length = 0;
    this.ringGeo.dispose();
    this.beamGeo.dispose();
    this.hudEl?.remove();
  }
}
